/**
 * GNews response normalization — provider quirks → canonical news dataShape.
 *
 * SERVER-SIDE ONLY. Validates the raw GNews payload (already Zod-parsed) and maps
 * it to the metrics declared in DATA_SHAPE_KEYS['news.headlines']. All normalization
 * happens here; `glanceView` receives clean, typed values.
 *
 * Immutability: returns a fresh object, never mutates the input.
 *
 * GNews API docs: https://gnews.io/docs/v4
 * Top-headlines endpoint: GET https://gnews.io/api/v4/top-headlines
 * Sample article shape documented at the reference above.
 */

import { z } from 'zod';

// ---------------------------------------------------------------------------
// Raw GNews response schema (validates the upstream payload before use).
// SERVER-SIDE ONLY — the client never sees raw provider payloads (ADR-002 §5).
// ---------------------------------------------------------------------------

/**
 * Zod schema for a single GNews article entry.
 * Ref: https://gnews.io/docs/v4#response
 *
 * We validate only the fields we consume; unknown additional fields are
 * permitted (passthrough) so future GNews additions don't break the adapter.
 */
export const rawGNewsArticleSchema = z.object({
  title: z.string(),
  source: z.object({
    name: z.string(),
    url: z.string().optional(),
  }),
  publishedAt: z.string(), // ISO 8601 datetime string
  url: z.string().optional(),
  description: z.string().optional(),
  content: z.string().optional(),
});

/**
 * Zod schema for the full GNews top-headlines API response.
 * Ref: https://gnews.io/docs/v4#response
 *
 * The `articles` array must have at least one entry (we requested news, not
 * an empty result). An empty array is an unexpected response; the adapter
 * represents it via itemCount=0 but still provides the dataShape.
 */
export const rawGNewsSchema = z.object({
  totalArticles: z.number().int().optional(),
  articles: z.array(rawGNewsArticleSchema),
});

export type RawGNews = z.infer<typeof rawGNewsSchema>;
export type RawGNewsArticle = z.infer<typeof rawGNewsArticleSchema>;

// ---------------------------------------------------------------------------
// Normalized news data shape — the canonical output of normalize().
// Keys MUST match DATA_SHAPE_KEYS['news.headlines']:
//   headline, source, publishedAt, itemCount
// ---------------------------------------------------------------------------

/**
 * Normalized news metrics. Exported so glanceView and tests can reference the
 * type without importing from the raw shape.
 *
 * `headline` and `source` represent the FIRST (top) article. This is the
 * design decision for the "latest headline" tile: we surface the top result.
 *
 * `publishedAt` is an ISO 8601 string from GNews; the renderer formats it.
 * `itemCount` is the count of articles returned in this batch.
 */
export interface NewsData {
  /** Headline of the first/top article. */
  readonly headline: string;
  /** Name of the news source for the top article. */
  readonly source: string;
  /** ISO 8601 datetime string of the top article's publication time. */
  readonly publishedAt: string;
  /** Total article count returned in this batch (≥0). */
  readonly itemCount: number;
}

/**
 * Map a GNews raw payload → canonical NewsData.
 *
 * Pure: returns a fresh object, never mutates `raw`.
 * Total: handles empty articles array (itemCount=0, empty headline/source).
 *
 * Design: surfaces the FIRST article as the primary headline. The itemCount
 * allows the renderer to show "and N more" in a list variant. publishedAt
 * is the raw ISO string from GNews — the renderer does relative formatting.
 */
export function normalize(raw: RawGNews): NewsData {
  const first = raw.articles[0];
  return {
    headline: first?.title ?? '',
    source: first?.source.name ?? '',
    publishedAt: first?.publishedAt ?? '',
    itemCount: raw.articles.length,
  };
}
